"use client";

import {
	getLoadAllBelowFoldSections,
	subscribeBelowFoldLoad,
} from "@marketing/home/lib/landing-below-fold-load-controller";
import { cn } from "@repo/ui";
import {
	type PropsWithChildren,
	useEffect,
	useRef,
	useState,
	useSyncExternalStore,
} from "react";

export interface LandingIntersectionDeferredSectionProps
	extends PropsWithChildren {
	/** Reserved min-height while the section chunk has not mounted (CLS guard). */
	minHeightClass: string;
	/** IntersectionObserver rootMargin; larger values load earlier before scroll. */
	rootMargin?: string;
	/** When true, mount children immediately (still client-only). */
	loadEager?: boolean;
	/** Fires once when this section becomes eligible to mount children. */
	onBecomeVisible?: () => void;
	className?: string;
}

function subscribeLoadAll(onStoreChange: () => void): () => void {
	return subscribeBelowFoldLoad(onStoreChange);
}

function getLoadAllSnapshot(): boolean {
	return getLoadAllBelowFoldSections();
}

/**
 * Mounts children only after the sentinel nears the viewport (once — stays mounted).
 * The wrapper always keeps `minHeightClass` so document height and scroll affordance
 * remain stable while dynamic chunks download.
 */
export function LandingIntersectionDeferredSection({
	children,
	minHeightClass,
	rootMargin = "0px 0px 200px 0px",
	loadEager = false,
	onBecomeVisible,
	className,
}: LandingIntersectionDeferredSectionProps) {
	const sentinelRef = useRef<HTMLDivElement>(null);
	const onBecomeVisibleRef = useRef(onBecomeVisible);
	onBecomeVisibleRef.current = onBecomeVisible;
	const loadAll = useSyncExternalStore(
		subscribeLoadAll,
		getLoadAllSnapshot,
		() => false,
	);
	const [shouldLoad, setShouldLoad] = useState(loadEager || loadAll);

	useEffect(() => {
		if (loadAll) {
			setShouldLoad(true);
		}
	}, [loadAll]);

	useEffect(() => {
		if (!shouldLoad) {
			return;
		}
		onBecomeVisibleRef.current?.();
	}, [shouldLoad]);

	useEffect(() => {
		if (loadEager || shouldLoad) {
			return;
		}
		const el = sentinelRef.current;
		if (!el) {
			return;
		}

		const observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					if (entry.isIntersecting) {
						setShouldLoad(true);
						observer.disconnect();
						break;
					}
				}
			},
			{ root: null, rootMargin, threshold: 0 },
		);

		observer.observe(el);
		return () => {
			observer.disconnect();
		};
	}, [loadEager, shouldLoad, rootMargin]);

	return (
		<div
			ref={sentinelRef}
			className={cn("w-full shrink-0", minHeightClass, className)}
		>
			{shouldLoad ? children : null}
		</div>
	);
}
